import Vue from 'vue';

const state = Vue.observable({
	notifications: []
});

let nextId = 1;

function findNotification(id) {
	return state.notifications.find(notification => notification.id === id);
}

function removeNotification(id) {
	const notification = findNotification(id);

	if (!notification) {
		return;
	}

	if (notification.animationFrame) {
		cancelAnimationFrame(notification.animationFrame);
	}

	const index = state.notifications.findIndex(item => item.id === id);

	if (index !== -1) {
		state.notifications.splice(index, 1);
	}
}

function updateTimer(id) {
	const notification = findNotification(id);

	if (!notification || notification.paused) {
		return;
	}

	const now = Date.now();
	const elapsed = now - notification.startedAt;

	notification.remaining = Math.max(
		notification.remainingAtStart - elapsed,
		0
	);

	notification.progress =
		notification.duration > 0
			? (notification.remaining / notification.duration) * 100
			: 0;

	if (notification.remaining <= 0) {
		removeNotification(id);
		return;
	}

	notification.animationFrame = requestAnimationFrame(() => {
		updateTimer(id);
	});
}

function startTimer(notification) {
	if (notification.duration <= 0) {
		return;
	}

	notification.startedAt = Date.now();
	notification.remainingAtStart = notification.remaining;
	notification.paused = false;

	notification.animationFrame = requestAnimationFrame(() => {
		updateTimer(notification.id);
	});
}

function pauseNotification(id) {
	const notification = findNotification(id);

	if (!notification || notification.paused) {
		return;
	}

	const elapsed = Date.now() - notification.startedAt;

	notification.remaining = Math.max(
		notification.remainingAtStart - elapsed,
		0
	);

	notification.progress =
		(notification.remaining / notification.duration) * 100;

	notification.paused = true;

	if (notification.animationFrame) {
		cancelAnimationFrame(notification.animationFrame);
		notification.animationFrame = null;
	}
}

function resumeNotification(id) {
	const notification = findNotification(id);

	if (
		!notification ||
		!notification.paused ||
		notification.remaining <= 0
	) {
		return;
	}

	startTimer(notification);
}

function showNotification(options) {
	const normalizedOptions =
		typeof options === 'string'
			? { message: options }
			: options || {};

	const duration =
		typeof normalizedOptions.duration === 'number'
			? normalizedOptions.duration
			: 5000;

	const notification = {
		id: nextId++,
		message: normalizedOptions.message || '',
		type: normalizedOptions.type || 'success',
		duration,
		remaining: duration,
		remainingAtStart: duration,
		progress: 100,
		paused: false,
		startedAt: null,
		animationFrame: null
	};

	state.notifications.push(notification);
	startTimer(notification);

	return notification.id;
}

const notifyGop = {
	show(options) {
		return showNotification(options);
	},

	success(message, options = {}) {
		return showNotification({
			...options,
			message,
			type: 'success'
		});
	},

	error(message, options = {}) {
		return showNotification({
			...options,
			message,
			type: 'error'
		});
	},

	warning(message, options = {}) {
		return showNotification({
			...options,
			message,
			type: 'warning'
		});
	},

	info(message, options = {}) {
		return showNotification({
			...options,
			message,
			type: 'info'
		});
	},

	close(id) {
		removeNotification(id);
	},

	clear() {
		state.notifications.slice().forEach(notification => {
			removeNotification(notification.id);
		});
	},

	pause(id) {
		pauseNotification(id);
	},

	resume(id) {
		resumeNotification(id);
	}
};

const NotificationPlugin = {
	install(VueInstance) {
		VueInstance.prototype.$notifyGop = notifyGop;
	}
};

export {
	state as notificationState,
	notifyGop,
	NotificationPlugin
};

export default NotificationPlugin;
